/**
 * ArtefactIllustration - Decorative illustration for artefact blocks
 *
 * A stylized document illustration with content lines and subtle shadow.
 * Designed to appear at the bottom-right of artefact cards.
 */

import { cn } from '@heidi/utils';

import type { ArtefactType } from '../../contracts';

import type { JSX } from 'react';

interface ArtefactIllustrationProps {
  /** Declared for the call sites; the drawing is the same for every type. */
  type: ArtefactType;
  className?: string;
}

/**
 * Stylized document illustration with content lines
 * Exported from Figma design
 */
export const ArtefactIllustration = ({
  className,
}: ArtefactIllustrationProps): JSX.Element => {
  return (
    <svg
      viewBox="0 0 234 235"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(className)}
      aria-hidden="true"
    >
      <g filter="url(#filter0_artefact)">
        {/* Document background */}
        <rect
          x="13.9988"
          y="8.74902"
          width="205.096"
          height="206.8"
          rx="6.99941"
          fill="white"
          shapeRendering="crispEdges"
        />
        {/* Title line */}
        <rect
          x="27.9977"
          y="22.748"
          width="124.932"
          height="14.0769"
          rx="3.4997"
          fill="#D4C4C9"
        />
        {/* Content lines */}
        <rect
          x="27.9977"
          y="43.8242"
          width="153.002"
          height="14.0769"
          rx="3.4997"
          fill="#E9E2E3"
        />
        <rect
          x="27.9977"
          y="64.9004"
          width="177.098"
          height="14.2179"
          rx="3.4997"
          fill="#E9E2E3"
        />
        <rect
          x="27.9977"
          y="93.1172"
          width="177.098"
          height="14.2179"
          rx="3.4997"
          fill="#D4C4C9"
        />
        <rect
          x="27.9977"
          y="114.334"
          width="103.039"
          height="14.4898"
          rx="3.4997"
          fill="#E9E2E3"
        />
        <rect
          x="27.9977"
          y="135.824"
          width="135.764"
          height="14.4898"
          rx="3.4997"
          fill="#E9E2E3"
        />
        <rect
          x="27.9977"
          y="157.313"
          width="108.34"
          height="14.4898"
          rx="3.4997"
          fill="#E9E2E3"
        />
        <rect
          x="27.9977"
          y="178.802"
          width="97.2571"
          height="14.4898"
          rx="3.4997"
          fill="#E9E2E3"
        />
      </g>
      <defs>
        <filter
          id="filter0_artefact"
          x="0"
          y="0"
          width="233.093"
          height="234.797"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="5.24956" />
          <feGaussianBlur stdDeviation="6.99941" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};
